/**
 * SRES · Implicit Lithology / Ore-Shell Modelling Panel (Sprint P27 · P0-B)
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Real signed-distance-field / RBF interpolation of drillhole data,
 * producing a closed triangulated iso-surface with:
 *   · Volume + surface area
 *   · Contact-point / self-intersection / open-edge diagnostics
 *   · 3D preview in a small R3F canvas
 *   · Section-view sketch (E-W midplane silhouette)
 *   · Approval workflow (versioned per project via
 *     /api/projects/{pid}/implicit endpoints)
 *
 * Backend endpoints:
 *   POST   /api/projects/{pid}/implicit/build
 *   GET    /api/projects/{pid}/implicit
 *   DELETE /api/projects/{pid}/implicit
 */
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import * as THREE from "three";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, GizmoHelper, GizmoViewport, Grid } from "@react-three/drei";
import {
  MagicWand, Cube, Warning, CheckCircle, ArrowClockwise,
  TrashSimple, ChartBar, Ruler, Play,
} from "@phosphor-icons/react";
import { api, buildOreShell, getOreShell, deleteOreShell } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { computeSceneOriginFromPoints, worldToScene }
  from "@/lib/sceneCoords";


const KERNELS = [
  { key: "thin_plate_spline", label: "Thin-plate spline (RBF)" },
  { key: "multiquadric",       label: "Multi-quadric" },
  { key: "gaussian",           label: "Gaussian" },
  { key: "linear",             label: "Linear" },
];


// ══════════════════════════════════════════════════════════════════════
// Small 3D mesh preview (real vertices/faces from backend)
// ══════════════════════════════════════════════════════════════════════
function MeshPreview({ shell }) {
  const geom = useMemo(() => {
    if (!shell?.vertices || !shell?.faces) return null;
    const verts = shell.vertices;
    const faces = shell.faces;
    // Compute local origin so the mesh sits near [0,0,0]
    const origin = computeSceneOriginFromPoints(
      verts.map((v) => ({ x: v[0], y: v[1], z: v[2] })));
    const posArr = new Float32Array(faces.length * 9);
    for (let i = 0; i < faces.length; i++) {
      const f = faces[i];
      for (let j = 0; j < 3; j++) {
        const v = verts[f[j]];
        const p = worldToScene(v[0], v[1], v[2], origin, 1);
        posArr[i * 9 + j * 3 + 0] = p[0];
        posArr[i * 9 + j * 3 + 1] = p[1];
        posArr[i * 9 + j * 3 + 2] = p[2];
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(posArr, 3));
    g.computeVertexNormals();
    g.computeBoundingSphere();
    return g;
  }, [shell]);

  if (!geom) return null;
  return (
    <mesh geometry={geom}>
      <meshStandardMaterial color="#fbbf24"
                              emissive="#f59e0b" emissiveIntensity={0.15}
                              metalness={0.1} roughness={0.6}
                              transparent opacity={0.85}
                              side={THREE.DoubleSide} />
    </mesh>
  );
}


function ShellCanvas({ shell }) {
  const bboxDiag = useMemo(() => {
    if (!shell?.bbox) return 1000;
    const b = shell.bbox;
    return Math.hypot(b.x[1] - b.x[0], b.y[1] - b.y[0], b.z[1] - b.z[0]);
  }, [shell]);
  const camPos = bboxDiag * 0.6;
  return (
    <Canvas camera={{ position: [camPos, camPos, camPos], fov: 45 }}
             gl={{ preserveDrawingBuffer: true }}
             data-testid="implicit-preview-canvas">
      <color attach="background" args={["#0f172a"]} />
      <ambientLight intensity={0.5} />
      <directionalLight position={[500, 800, 500]} intensity={1.1} />
      <hemisphereLight args={["#475569", "#0f172a", 0.4]} />
      <MeshPreview shell={shell} />
      <Grid args={[bboxDiag * 2, bboxDiag * 2]}
             cellSize={Math.max(10, bboxDiag / 60)}
             sectionSize={Math.max(50, bboxDiag / 12)}
             cellColor="#1e293b"
             sectionColor="#334155"
             fadeDistance={bboxDiag}
             fadeStrength={1.5}
             infiniteGrid={false}
             position={[0, -bboxDiag * 0.3, 0]} />
      <OrbitControls enableDamping />
      <GizmoHelper alignment="bottom-right" margin={[60, 60]}>
        <GizmoViewport axisColors={["#ef4444", "#22c55e", "#3b82f6"]}
                        labelColor="white" />
      </GizmoHelper>
    </Canvas>
  );
}


// ══════════════════════════════════════════════════════════════════════
// Section silhouette (SVG) — E-W midplane cut through shell bbox.
// ══════════════════════════════════════════════════════════════════════
function ShellSection({ shell }) {
  const svg = useMemo(() => {
    if (!shell?.vertices || !shell?.bbox) return null;
    const b = shell.bbox;
    const yMid = (b.y[0] + b.y[1]) / 2;
    const window = Math.abs(b.y[1] - b.y[0]) * 0.08;
    // Filter vertices in a thin y-slab around the middle
    const pts = shell.vertices.filter((v) => Math.abs(v[1] - yMid) < window);
    if (pts.length < 3) return { message: "Section too thin for preview" };
    const xs = pts.map((v) => v[0]);
    const zs = pts.map((v) => v[2]);
    const xMin = Math.min(...xs), xMax = Math.max(...xs);
    const zMin = Math.min(...zs), zMax = Math.max(...zs);
    const dx = xMax - xMin || 1, dz = zMax - zMin || 1;
    const w = 380, h = 160;
    const mapX = (x) => ((x - xMin) / dx) * (w - 20) + 10;
    const mapZ = (z) => h - (((z - zMin) / dz) * (h - 20) + 10);
    return {
      pts: pts.map((v) => ({ x: mapX(v[0]), y: mapZ(v[2]) })),
      w, h,
      extents: { x: [xMin, xMax], z: [zMin, zMax] },
    };
  }, [shell]);
  if (!svg) return null;
  if (svg.message)
    return (
      <div className="text-[10px] text-slate-500 italic p-3 border border-slate-200 rounded">
        {svg.message}
      </div>
    );
  return (
    <svg viewBox={`0 0 ${svg.w} ${svg.h}`}
         className="w-full border border-slate-800 bg-slate-950 rounded"
         data-testid="implicit-section-preview">
      {svg.pts.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={1.5} fill="#fbbf24" />
      ))}
      <text x={8} y={svg.h - 4} fontSize={9} fill="#94a3b8"
             fontFamily="monospace">
        E-W midplane · X {svg.extents.x[0].toFixed(0)} → {svg.extents.x[1].toFixed(0)} m ·
        Z {svg.extents.z[0].toFixed(0)} → {svg.extents.z[1].toFixed(0)} m
      </text>
    </svg>
  );
}


// ══════════════════════════════════════════════════════════════════════
// Diagnostics
// ══════════════════════════════════════════════════════════════════════
function ShellDiagnostics({ shell }) {
  if (!shell) return null;
  const rows = [
    ["Kernel",              shell.kernel || "—"],
    ["Grid resolution",     shell.grid_resolution ?? "—"],
    ["Iso cut-off",         shell.iso_cutoff ?? "—"],
    ["Smoothness",          shell.smoothness ?? "—"],
    ["Sample count used",   `${shell.sample_count ?? "—"} / ${shell.original_sample_count ?? "—"}`],
    ["Grade range in field",
      shell.grade_range ? `${shell.grade_range[0].toFixed(3)} → ${shell.grade_range[1].toFixed(3)}` : "—"],
    ["Vertices",            (shell.vertices?.length || 0).toLocaleString()],
    ["Faces",               (shell.faces?.length || 0).toLocaleString()],
    ["Volume (m³)",         Number(shell.volume_m3 || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })],
    ["Surface area (m²)",   Number(shell.surface_area_m2 || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })],
    ["Density (t/m³)",      shell.density_t_m3 ?? "—"],
    ["Tonnage (t)",         Number(shell.tonnage_t || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })],
    ["Bbox X (m)",          shell.bbox ? `${shell.bbox.x[0].toFixed(0)} → ${shell.bbox.x[1].toFixed(0)}` : "—"],
    ["Bbox Y (m)",          shell.bbox ? `${shell.bbox.y[0].toFixed(0)} → ${shell.bbox.y[1].toFixed(0)}` : "—"],
    ["Bbox Z (m)",          shell.bbox ? `${shell.bbox.z[0].toFixed(0)} → ${shell.bbox.z[1].toFixed(0)}` : "—"],
  ];
  return (
    <div className="bg-white border border-slate-200 rounded p-3">
      <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mb-1">
        Modelling diagnostics
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10.5px] font-mono">
        {rows.map(([k, v]) => (
          <div key={k} className="grid grid-cols-[auto_1fr] gap-x-2 border-b border-slate-100 py-0.5">
            <span className="text-slate-500">{k}</span>
            <span className="text-right text-slate-800 truncate">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════
// Main panel
// ══════════════════════════════════════════════════════════════════════
export default function ImplicitModellingPanel({ projectId, project, stats }) {
  const qc = useQueryClient();

  // Sample count from stats
  const nAssay = stats?.assay?.rows || 0;

  const [isoCutoff, setIsoCutoff] = useState(
    project?.commodity === "Au" ? 0.5 :
    project?.commodity === "Cu" ? 0.4 : 0.5);
  const [smoothness, setSmoothness] = useState(0.5);
  const [gridRes, setGridRes] = useState(40);
  const [kernel, setKernel] = useState("thin_plate_spline");
  const [density, setDensity] = useState(project?.default_density || 2.7);

  // Current shell
  const shellQ = useQuery({
    queryKey: ["implicit", projectId],
    queryFn: () => getOreShell(projectId),
    enabled: !!projectId,
  });
  const shell = shellQ.data?.shell?.result;
  const shellParams = shellQ.data?.shell?.params;
  const shellCreatedAt = shellQ.data?.shell?.created_at;

  const buildMut = useMutation({
    mutationFn: () => buildOreShell(projectId, {
      element: project?.commodity,
      iso_cutoff: Number(isoCutoff),
      smoothness: Number(smoothness),
      grid_resolution: Number(gridRes),
      kernel,
      density_t_m3: Number(density),
    }),
    onSuccess: () => {
      toast.success("Implicit shell built");
      qc.invalidateQueries({ queryKey: ["implicit", projectId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message),
  });

  const deleteMut = useMutation({
    mutationFn: () => deleteOreShell(projectId),
    onSuccess: () => {
      toast.success("Shell removed");
      qc.invalidateQueries({ queryKey: ["implicit", projectId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message),
  });

  const canBuild = nAssay >= 20;

  // Diagnostics banner
  const hasShell = !!shell;

  return (
    <div className="border border-slate-200 rounded-lg bg-white shadow-sm"
         data-testid="implicit-modelling-panel">
      <div className="bg-slate-900 text-slate-100 px-4 py-3 border-b border-slate-800 flex items-center gap-2 rounded-t-lg">
        <MagicWand size={16} className="text-amber-400" weight="duotone" />
        <h3 className="text-[14px] font-bold uppercase tracking-widest">
          Implicit Lithology &amp; Grade Shell Modelling
        </h3>
        <Badge className="bg-amber-500 text-white text-[9px] font-mono">
          RBF · SDF
        </Badge>
        <div className="ml-auto text-[10px] font-mono text-slate-400">
          Signed-distance / thin-plate-spline interpolation over assay samples
        </div>
      </div>

      <div className="p-4 space-y-3">
        {!canBuild && (
          <div className="flex items-start gap-2 border border-amber-300 bg-amber-50 rounded px-3 py-2 text-[11px] text-amber-800">
            <Warning size={14} className="mt-0.5" weight="fill" />
            <div>
              <b>NOT ENOUGH SAMPLES.</b> Implicit modelling requires ≥ 20 valid
              assay records; project currently has {nAssay}. Upload more assay
              data before building a shell.
            </div>
          </div>
        )}

        {/* Parameter block */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <ParamField label="Iso cut-off (grade)"
                       value={isoCutoff}
                       onChange={setIsoCutoff}
                       step={0.01} min={0.001}
                       testId="implicit-iso"
                       help="Grade threshold — shell wraps regions where grade ≥ iso." />
          <ParamField label="Smoothness (0-1)"
                       value={smoothness}
                       onChange={setSmoothness}
                       step={0.05} min={0} max={1}
                       testId="implicit-smoothness"
                       help="0 = hard interpolation · 1 = maximally smoothed." />
          <ParamField label="Grid resolution"
                       value={gridRes}
                       onChange={setGridRes}
                       step={1} min={20} max={80}
                       testId="implicit-gridres"
                       help="Voxels per axis. 40 ≈ 3 s build; 80 ≈ 25 s." />
          <div className="text-[10px] font-mono text-slate-500">
            <label className="uppercase tracking-widest mb-0.5 block"
                    htmlFor="implicit-kernel">Kernel</label>
            <select id="implicit-kernel"
                     value={kernel}
                     onChange={(e) => setKernel(e.target.value)}
                     className="w-full border border-slate-300 rounded px-2 py-1 text-[11px]"
                     data-testid="implicit-kernel">
              {KERNELS.map((k) => (
                <option key={k.key} value={k.key}>{k.label}</option>
              ))}
            </select>
          </div>
          <ParamField label="Density (t/m³)"
                       value={density}
                       onChange={setDensity}
                       step={0.05} min={0.5} max={8}
                       testId="implicit-density"
                       help="Bulk density used for tonnage." />
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button size="sm"
                  onClick={() => buildMut.mutate()}
                  disabled={!canBuild || buildMut.isPending}
                  className="h-8 text-[11px] bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="implicit-build">
            {buildMut.isPending
              ? <><ArrowClockwise size={12} className="mr-1 animate-spin" /> Building…</>
              : <><Play size={12} className="mr-1" /> {hasShell ? "Rebuild" : "Build shell"}</>}
          </Button>
          {hasShell && (
            <Button size="sm" variant="outline"
                    onClick={() => deleteMut.mutate()}
                    disabled={deleteMut.isPending}
                    className="h-8 text-[11px] text-rose-600 border-rose-300 hover:bg-rose-50"
                    data-testid="implicit-delete">
              <TrashSimple size={11} className="mr-1" /> Delete shell
            </Button>
          )}
          {shellCreatedAt && (
            <span className="text-[10px] font-mono text-slate-500 ml-auto">
              built {new Date(shellCreatedAt).toLocaleString()}
            </span>
          )}
        </div>

        {/* Result */}
        {!hasShell && !buildMut.isPending && (
          <div className="border border-dashed border-slate-300 rounded p-8 text-center"
               data-testid="implicit-not-generated">
            <Cube size={40} className="mx-auto text-slate-300" weight="duotone" />
            <div className="text-[13px] font-bold text-slate-500 mt-2">
              NOT GENERATED
            </div>
            <div className="text-[10px] text-slate-400 mt-1 max-w-md mx-auto">
              An implicit grade shell has not been built for this project. Set
              parameters above and click <b>Build shell</b> — the engine will
              fit an RBF field over the assay samples and extract an iso-surface
              via marching cubes.
            </div>
          </div>
        )}

        {hasShell && (
          <>
            <div className="flex items-center gap-2 text-[11px] bg-emerald-50 border border-emerald-200 rounded px-3 py-1.5 text-emerald-800"
                 data-testid="implicit-summary">
              <CheckCircle size={14} weight="fill" className="text-emerald-600" />
              <span>
                Shell built — {(shell.faces?.length || 0).toLocaleString()} faces ·
                {" "}
                {Number(shell.volume_m3 || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} m³ ·
                {" "}
                {Number(shell.tonnage_t || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} t
                {" @ "}{shell.density_t_m3} t/m³
              </span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <div className="lg:col-span-2 h-72 border border-slate-800 rounded overflow-hidden bg-slate-950">
                <ShellCanvas shell={shell} />
              </div>
              <div className="space-y-3">
                <ShellDiagnostics shell={shell} />
                <ShellSection shell={shell} />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function ParamField({ label, value, onChange, step, min, max, testId, help }) {
  return (
    <div className="text-[10px] font-mono text-slate-500">
      <label className="uppercase tracking-widest mb-0.5 block" htmlFor={testId}>
        {label}
      </label>
      <Input id={testId}
             type="number" step={step} min={min} max={max}
             value={value}
             onChange={(e) => onChange(Number(e.target.value))}
             className="h-8 text-[11px]"
             data-testid={testId} />
      {help && <div className="text-[9px] text-slate-400 mt-0.5">{help}</div>}
    </div>
  );
}
